
import {Component, Prop, Vue} from 'vue-property-decorator';
import {ADDRESSES, checkImages, identifyFiles, md5Hex} from '@/helpers/esp-flash-files';
import {ESPTOOL_BASE, loadEsptool} from '@/helpers/esptool-loader';

/**
 * Flash USB et console série d'un datalogger ESP32-EVB-EA depuis le navigateur (Web Serial + esptool-js),
 * intégré à l'onglet Datalogger de la fiche compteur.
 * Les fichiers ne quittent pas le poste. Voir docker-stack/plans/flash-usb-navigateur-datalogger.md.
 */

const FLASH_BAUDRATE = 460800;
const ROM_BAUDRATE = 115200;
const CONSOLE_BAUDRATE = 115200;
const CONSOLE_MAX_CHARS = 300000;

type AlertType = 'success' | 'info' | 'warning' | 'error';

interface Message {
  type: AlertType;
  text: string;
}

interface FlashPart {
  name: string;
  file: string;
  address: number;
  data: Uint8Array;
  md5: string;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

@Component({})
export default class DataloggerUsbFlash extends Vue {
  /** MAC du boîtier du compteur : le flash est refusé si le boîtier branché en a une autre */
  @Prop({default: null})
  expectedMac: string | null;

  supported: boolean = 'serial' in navigator && window.isSecureContext;

  // Port série choisi par l'utilisateur, partagé entre le flash et la console (un seul usage à la fois)
  port: any = null;

  // 1. Fichiers
  fileMessages: Message[] = [];
  parts: FlashPart[] = null;
  candidateParts: FlashPart[] = null;
  blockingMessages: string[] = [];
  blockingAccepted: boolean = false;
  firmwareInfo: string = '';

  // 2. Flash
  eraseAll: boolean = false;
  flashing: boolean = false;
  flashProgress: number = 0;
  flashMessages: Message[] = [];
  flashLog: string = '';

  // 3. Console
  consoleOpen: boolean = false;
  consoleReader: any = null;
  consoleText: string = '';
  consoleMessages: Message[] = [];

  beforeDestroy(): void {
    this.closeConsole();
  }

  mounted(): void {
    if (this.supported) {
      (navigator as any).serial.addEventListener('disconnect', this.onSerialDisconnect);
    }
  }

  destroyed(): void {
    if (this.supported) {
      (navigator as any).serial.removeEventListener('disconnect', this.onSerialDisconnect);
    }
  }

  onSerialDisconnect(event: Event): void {
    if (event.target === this.port) {
      this.consoleMessages.push({type: 'warning', text: 'Boîtier débranché.'});
      this.closeConsole();
      this.port = null;
    }
  }

  async getPort(): Promise<any> {
    if (!this.port) {
      this.port = await (navigator as any).serial.requestPort();
    }
    return this.port;
  }

  hex(n: number): string {
    return `0x${n.toString(16)}`;
  }

  errorText(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  // ************************************
  // 1. Fichiers
  // ************************************

  pickFolder(): void {
    (this.$refs.folderInput as HTMLInputElement).click();
  }

  pickFiles(): void {
    (this.$refs.filesInput as HTMLInputElement).click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.loadFiles(Array.from(input.files || []));
    input.value = '';
  }

  onBlockingAccepted(accepted: boolean): void {
    this.blockingAccepted = accepted;
    this.parts = accepted ? this.candidateParts : null;
  }

  async loadFiles(files: File[]): Promise<void> {
    this.fileMessages = [];
    this.parts = null;
    this.candidateParts = null;
    this.blockingMessages = [];
    this.blockingAccepted = false;
    this.firmwareInfo = '';

    if (files.length === 0) {
      return;
    }

    const identified = identifyFiles(files);
    identified.ignored
        .filter((ignored) => ignored.reason !== 'non utilisé')
        .forEach((ignored) => this.fileMessages.push({type: 'info', text: `${ignored.file.name} ignoré : ${ignored.reason}.`}));
    if (identified.errors.length > 0) {
      identified.errors.forEach((error) => this.fileMessages.push({type: 'error', text: error}));
      return;
    }

    const read = async (file: File): Promise<Uint8Array> => new Uint8Array(await file.arrayBuffer());
    const bootApp0Response = await fetch(`${ESPTOOL_BASE}boot_app0.bin`);
    if (!bootApp0Response.ok) {
      this.fileMessages.push({type: 'error', text: 'Impossible de charger boot_app0.bin.'});
      return;
    }

    const images = {
      bootloader: await read(identified.bootloader),
      partitions: await read(identified.partitions),
      app: await read(identified.app),
    };
    const bootApp0 = new Uint8Array(await bootApp0Response.arrayBuffer());

    const check = checkImages(images);
    check.errors.forEach((error) => this.fileMessages.push({type: 'error', text: error}));
    check.warnings.forEach((warning) => this.fileMessages.push({type: 'warning', text: warning}));
    if (check.errors.length > 0) {
      return;
    }

    const part = (name: string, file: string, address: number, data: Uint8Array): FlashPart => ({name, file, address, data, md5: md5Hex(data)});
    this.candidateParts = [
      part('Bootloader', identified.bootloader.name, ADDRESSES.bootloader, images.bootloader),
      part('Partitions', identified.partitions.name, ADDRESSES.partitions, images.partitions),
      part('boot_app0', 'boot_app0.bin (fourni par la plateforme)', ADDRESSES.bootApp0, bootApp0),
      part('Application', identified.app.name, ADDRESSES.app, images.app),
    ];

    const usage = check.info.appUsage !== null ? ` · application : ${(check.info.appUsage * 100).toFixed(0)} % de l'emplacement OTA` : '';
    this.firmwareInfo = `Version du firmware : ${check.info.version || 'inconnue'}${usage}`;

    if (check.blocking.length > 0) {
      this.blockingMessages = check.blocking;
      return;
    }

    this.fileMessages.push({type: 'success', text: 'Fichiers prêts à être flashés.'});
    this.parts = this.candidateParts;
  }

  // ************************************
  // 2. Flash
  // ************************************

  async requestFlash(): Promise<void> {
    if (!this.parts) {
      return;
    }
    if (this.eraseAll) {
      const confirmed = await this.$dialog.confirm({
        title: 'Effacer toute la mémoire',
        text: 'Effacer toute la mémoire du boîtier, y compris ses identifiants Wi-Fi ? Il repartira avec ceux par défaut du sketch.',
        actions: [{text: 'Annuler', key: false}, {text: 'Effacer et flasher', key: true}],
      });
      if (!confirmed) {
        return;
      }
    }
    await this.flash();
  }

  appendFlashLog(text: string): void {
    this.flashLog += text;
    this.$nextTick(() => {
      const element = this.$refs.flashLog as HTMLElement;
      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    });
  }

  async flash(): Promise<void> {
    const parts = this.parts;
    this.flashMessages = [];
    this.flashLog = '';
    this.flashProgress = 0;
    this.flashing = true;

    const terminal = {
      clean: (): void => {
        this.flashLog = '';
      },
      writeLine: (line: string): void => this.appendFlashLog(`${line}\n`),
      write: (text: string): void => this.appendFlashLog(text),
    };

    let transport: any = null;
    try {
      if (this.consoleOpen) {
        await this.closeConsole();
      }
      const esptool = await loadEsptool();
      const device = await this.getPort();
      transport = new esptool.Transport(device, false);
      const loader = new esptool.ESPLoader({transport, baudrate: FLASH_BAUDRATE, romBaudrate: ROM_BAUDRATE, terminal});

      const chip = await loader.main();
      if (loader.chip.CHIP_NAME !== 'ESP32') {
        throw new Error(`Puce détectée : ${chip}. Ce firmware est prévu pour un ESP32 (ESP32-EVB).`);
      }
      const mac = String(await loader.chip.readMac(loader)).toUpperCase();
      this.flashMessages.push({type: 'info', text: `Boîtier détecté : ${chip} · MAC ${mac} (valeur du champ « MAC du datalogger » du compteur).`});
      if (this.expectedMac && mac !== this.expectedMac.toUpperCase()) {
        throw new Error(`Ce boîtier (${mac}) n'est pas celui du compteur (${this.expectedMac.toUpperCase()}). Flash annulé : vérifier le boîtier branché.`);
      }

      const total = parts.reduce((sum, p) => sum + p.data.length, 0);
      const offsets = parts.map((p, index) => parts.slice(0, index).reduce((sum, previous) => sum + previous.data.length, 0));

      await loader.writeFlash({
        fileArray: parts.map((p) => ({data: p.data, address: p.address})),
        flashMode: 'keep',
        flashFreq: 'keep',
        flashSize: 'keep',
        eraseAll: this.eraseAll,
        compress: true,
        reportProgress: (fileIndex: number, written: number, size: number) => {
          this.flashProgress = ((offsets[fileIndex] + (written / size) * parts[fileIndex].data.length) / total) * 100;
        },
        calculateMD5Hash: (image: Uint8Array) => md5Hex(image),
      });

      this.flashProgress = 100;
      await loader.after('hard_reset');
      this.flashMessages.push({type: 'success', text: 'Flash terminé et vérifié (MD5). Le boîtier redémarre : ouvrir la console pour suivre son démarrage.'});
    } catch (error) {
      this.flashMessages.push({type: 'error', text: `Échec : ${this.errorText(error)}`});
      if (error && error.name === 'NotFoundError') {
        // Aucun port choisi dans la fenêtre du navigateur
        this.port = null;
      }
    } finally {
      if (transport) {
        await transport.disconnect().catch(() => undefined);
      }
      this.flashing = false;
    }
  }

  // ************************************
  // 3. Console série
  // ************************************

  appendConsole(text: string): void {
    const element = this.$refs.console as HTMLElement;
    const atBottom = !element || element.scrollTop + element.clientHeight >= element.scrollHeight - 20;
    this.consoleText += text;
    if (this.consoleText.length > CONSOLE_MAX_CHARS) {
      this.consoleText = this.consoleText.slice(-CONSOLE_MAX_CHARS);
    }
    if (atBottom) {
      this.$nextTick(() => {
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      });
    }
  }

  async openConsole(): Promise<void> {
    this.consoleMessages = [];
    try {
      const device = await this.getPort();
      await device.open({baudRate: CONSOLE_BAUDRATE});
      this.consoleOpen = true;
      this.appendConsole(`\n--- Console ouverte (${CONSOLE_BAUDRATE} bauds) ---\n`);

      const decoder = new TextDecoder();
      this.consoleReader = device.readable.getReader();
      while (this.consoleOpen) {
        const {value, done} = await this.consoleReader.read();
        if (done) {
          break;
        }
        this.appendConsole(decoder.decode(value, {stream: true}));
      }
    } catch (error) {
      if (this.consoleOpen) {
        this.consoleMessages.push({type: 'error', text: `Console interrompue : ${this.errorText(error)}`});
      } else if (error && error.name === 'NotFoundError') {
        this.port = null;
      } else if (error) {
        this.consoleMessages.push({type: 'error', text: this.errorText(error)});
      }
    } finally {
      await this.closeConsole();
    }
  }

  async closeConsole(): Promise<void> {
    if (!this.consoleOpen && !this.consoleReader) {
      return;
    }
    // Remis à zéro avant tout await : la boucle de lecture rappelle closeConsole en se terminant
    this.consoleOpen = false;
    const reader = this.consoleReader;
    this.consoleReader = null;
    if (reader) {
      await reader.cancel().catch(() => undefined);
      try {
        reader.releaseLock();
      } catch (e) {
        // déjà libéré
      }
    }
    if (this.port) {
      await this.port.close().catch(() => undefined);
    }
    this.appendConsole('\n--- Console fermée ---\n');
  }

  /** Impulsion sur EN via RTS (circuit de reset automatique), IO0 laissé haut : redémarrage normal */
  async resetDevice(): Promise<void> {
    if (!this.consoleOpen || !this.port) {
      return;
    }
    await this.port.setSignals({dataTerminalReady: false, requestToSend: true});
    await sleep(100);
    await this.port.setSignals({requestToSend: false});
    this.appendConsole('\n--- Redémarrage demandé ---\n');
  }

  clearConsole(): void {
    this.consoleText = '';
  }

  downloadConsole(): void {
    const blob = new Blob([this.consoleText], {type: 'text/plain'});
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `datalogger-console-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
}
